import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvoices, useSendInvoice, useMarkPaidInvoice, useVoidInvoice, useReissueInvoice } from './hooks/useInvoices';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useAuthStore } from '../auth/store';
import { useMerchants } from '../merchants/hooks/useMerchants';
import { InvoiceStatusBadge, InvoiceDocumentTypeBadge } from './InvoiceStatusBadge';
import { CreateInvoiceModal } from './CreateInvoiceModal';
import { BackdatedReceiptModal } from './BackdatedReceiptModal';
import Button from '../../shared/components/Button';
import type { Invoice } from './types';
import { isReceipt, effectiveStatus, isOverdue } from './lib/billing';
import { ExportButton } from "../../shared/components/ExportButton";

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatAmount(kobo: number, currency = 'NGN') {
  const symbol = currency === 'NGN' ? '₦' : '$';
  return symbol + (kobo / 100).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_FILTERS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Sent', value: 'SENT' },
  { label: 'Paid', value: 'PAID' },
  { label: 'Overdue', value: 'OVERDUE' },
  { label: 'Void', value: 'VOID' },
];

// Invoices vs. Receipts are distinct document types (PE-2105), not just a status. The backend
// list endpoint has no documentType filter param in this ticket's contract, so this narrows
// the already-fetched page client-side — the status filter above still drives the server query.
const DOCUMENT_TYPE_TABS: { label: string; value: 'ALL' | 'INVOICES' | 'RECEIPTS' }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Invoices', value: 'INVOICES' },
  { label: 'Receipts', value: 'RECEIPTS' },
];

function InvoiceActions({ invoice }: { invoice: Invoice }) {
  const { hasPermission } = usePermissions();
  const sendMutation = useSendInvoice();
  const markPaidMutation = useMarkPaidInvoice();
  const voidMutation = useVoidInvoice();
  const reissueMutation = useReissueInvoice();

  const canUpdate = hasPermission(Permission.INVOICE_UPDATE);
  const canVoid = hasPermission(Permission.INVOICE_VOID);

  if (!canUpdate && !canVoid) return null;

  const status = effectiveStatus(invoice);

  return (
    <div className="flex items-center gap-2">
      {canUpdate && invoice.status === 'DRAFT' && (
        <button
          onClick={(e) => { e.stopPropagation(); sendMutation.mutate(invoice.id); }}
          disabled={sendMutation.isPending}
          className="text-xs text-blue-600 hover:underline disabled:opacity-50"
        >
          Send
        </button>
      )}
      {canUpdate && invoice.status === 'SENT' && (
        <button
          onClick={(e) => { e.stopPropagation(); markPaidMutation.mutate(invoice.id); }}
          disabled={markPaidMutation.isPending}
          className="text-xs text-green-600 hover:underline disabled:opacity-50"
        >
          Mark Paid
        </button>
      )}
      {canUpdate && status === 'EXPIRED' && (
        <button
          onClick={(e) => { e.stopPropagation(); reissueMutation.mutate(invoice.id); }}
          disabled={reissueMutation.isPending}
          className="text-xs text-amber-700 hover:underline disabled:opacity-50"
        >
          Reissue
        </button>
      )}
      {canVoid && (invoice.status === 'DRAFT' || invoice.status === 'SENT') && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Are you sure you want to void this invoice?')) {
              voidMutation.mutate(invoice.id);
            }
          }}
          disabled={voidMutation.isPending}
          className="text-xs text-red-500 hover:underline disabled:opacity-50"
        >
          Void
        </button>
      )}
    </div>
  );
}

export default function Invoices() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [statusFilter, setStatusFilter] = useState('');
  const [docTypeTab, setDocTypeTab] = useState<'ALL' | 'INVOICES' | 'RECEIPTS'>('ALL');
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateReceipt, setShowCreateReceipt] = useState(false);

  const { user } = useAuthStore();
  const isCollectiveStaff = user?.roleScope === 'COLLECTIVE';
  const { data: merchantsData } = useMerchants();

  const merchantMap = useMemo(() => {
    if (!isCollectiveStaff || !merchantsData) return undefined;
    return new Map<string, string>(merchantsData.map(m => [m.id, m.name]));
  }, [isCollectiveStaff, merchantsData]);

  // Endless scroll states
  const [page, setPage] = useState(1);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const queryParams = {
    ...(statusFilter ? { status: statusFilter } : {}),
    page,
    limit: 15,
  };

  const { data, isLoading, error } = useInvoices(queryParams);
  const invoices: Invoice[] = useMemo(() => {
    if (docTypeTab === 'ALL') return allInvoices;
    return allInvoices.filter((inv) => (docTypeTab === 'RECEIPTS' ? isReceipt(inv) : !isReceipt(inv)));
  }, [allInvoices, docTypeTab]);

  // Accumulate invoices
  useEffect(() => {
    if (data?.data && Array.isArray(data.data)) {
      if (page > 1) {
        setAllInvoices(prev => {
          const newInvoices = data.data.filter(newInv =>
            !prev.some(existingInv => existingInv.id === newInv.id)
          );
          const updated = [...prev, ...newInvoices];
          setHasMore(updated.length < data.total);
          return updated;
        });
        setIsLoadingMore(false);
      } else {
        setAllInvoices(data.data);
        setHasMore(data.data.length < data.total);
      }
    } else if (!isLoading && data?.data && data.data.length === 0) {
      if (page === 1) {
        setAllInvoices([]);
      }
      setHasMore(false);
    }
  }, [data, page, isLoading]);

  // Reset page when filter changes
  const handleFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(1);
    setHasMore(true);
    setIsLoadingMore(false);
  };

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore && data?.total && allInvoices.length < data.total) {
          setIsLoadingMore(true);
          setPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, data?.total, allInvoices.length]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Invoices &amp; Receipts</h1>
        <div className="flex gap-2">
          {/* PE-2127 — platform-wide export. */}
          <ExportButton endpoint="/invoices/export" filenameBase="invoices" />
          {hasPermission(Permission.INVOICE_CREATE) && (
            <>
              <Button variant="outline" onClick={() => setShowCreateReceipt(true)}>Issue Receipt</Button>
              <Button onClick={() => setShowCreate(true)}>Issue Invoice</Button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {DOCUMENT_TYPE_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setDocTypeTab(t.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              docTypeTab === t.value
                ? 'bg-purple-600 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => handleFilterChange(f.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === f.value
                ? 'bg-[#28282A] text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="text-center py-16 text-muted-foreground">Loading invoices…</div>
      )}

      {error && (
        <div className="text-center py-16 text-red-500">Failed to load invoices. Please try again.</div>
      )}

      {!isLoading && !error && invoices.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">No invoices found.</div>
      )}

      {!isLoading && !error && invoices.length > 0 && (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Doc #</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Tenant</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Solution</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Amount</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Due</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Created</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {invoices.map((inv) => {
                const receipt = isReceipt(inv);
                const status = effectiveStatus(inv);
                const overdue = !receipt && isOverdue(inv);
                return (
                  <tr
                    key={inv.id}
                    className="hover:bg-muted cursor-pointer transition-colors"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                  >
                    <td className="px-6 py-4 text-sm text-foreground font-mono">{inv.invoiceNumber}</td>
                    <td className="px-6 py-4"><InvoiceDocumentTypeBadge isReceipt={receipt} /></td>
                    <td className="px-6 py-4 text-sm text-muted-foreground max-w-[140px] truncate" title={inv.tenantId}>
                      {isCollectiveStaff && merchantMap ? (merchantMap.get(inv.tenantId) || inv.tenantId) : inv.tenantId}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{inv.solutionId}</td>
                    <td className="px-6 py-4 text-sm font-medium text-foreground">{formatAmount(inv.totalAmount, inv.currency)}</td>
                    <td className="px-6 py-4"><InvoiceStatusBadge status={status} /></td>
                    <td className="px-6 py-4 text-sm">
                      {receipt ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className={overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                          {formatDate(inv.dueDate)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(inv.createdAt)}</td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <InvoiceActions invoice={inv} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div ref={sentinelRef} className="flex justify-center py-6 border-t border-border">
            {(isLoadingMore || (isLoading && allInvoices.length > 0)) && (
              <div className="w-6 h-6 border-2 border-border border-t-gray-800 rounded-full animate-spin" />
            )}
            {!hasMore && allInvoices.length > 0 && !isLoadingMore && (
              <span className="text-xs text-muted-foreground">Showing all {allInvoices.length}</span>
            )}
          </div>
        </div>
      )}

      {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)} />}
      {showCreateReceipt && <BackdatedReceiptModal onClose={() => setShowCreateReceipt(false)} />}
    </div>
  );
}
